import type { RefObject } from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { FormErrorSummary } from "~/components/form-fields/FormErrorSummary";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { PasskeyIcon } from "~/components/ui/icons";
import { AuthPageFooter } from "./auth-page-footer";
import { AuthPageHeader } from "./auth-page-header";
import { Spinner } from "./shared";

type LoginPasskeyStepProps = {
  email: string;
  errorMessage: string;
  isHydrated: boolean;
  isLoading: boolean;
  loginReasonMessage: string | null;
  onUseDifferentEmail: () => void;
  onUsePasskey: () => void;
  onUsePassword: () => void;
  passkeyButtonRef: RefObject<HTMLButtonElement | null>;
  signupHref: string;
};

export function LoginPasskeyStep({
  email,
  errorMessage,
  isHydrated,
  isLoading,
  loginReasonMessage,
  onUseDifferentEmail,
  onUsePasskey,
  onUsePassword,
  passkeyButtonRef,
  signupHref,
}: LoginPasskeyStepProps) {
  const { i18n } = useLingui();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        <AuthPageHeader
          eyebrow={i18n._(msg`Quick sign-in available`)}
          title={i18n._(msg`Sign in without your password`)}
          description={email}
        />

        {loginReasonMessage ? (
          <Alert>
            <AlertDescription>{loginReasonMessage}</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-col gap-4">
          <div className="border-primary/20 bg-primary/5 rounded-lg border-2 p-4">
            <div className="mb-3 flex items-center gap-2">
              <PasskeyIcon className="text-primary size-5" />
              <span className="font-medium">{i18n._(msg`Use your passkey`)}</span>
            </div>
            <p className="text-muted-foreground mb-4 text-sm">
              {i18n._(msg`Use your fingerprint, face, or screen lock.`)}
            </p>
            <Button
              ref={passkeyButtonRef}
              className="w-full"
              size="lg"
              onClick={onUsePasskey}
              disabled={isLoading || !isHydrated}
            >
              {isLoading ? (
                <>
                  <Spinner className="mr-2" />
                  {i18n._(msg`Signing in...`)}
                </>
              ) : (
                <>
                  <PasskeyIcon className="mr-2 size-4" />
                  {i18n._(msg`Sign in with passkey`)}
                </>
              )}
            </Button>
          </div>

          {errorMessage ? <FormErrorSummary errors={[errorMessage]} /> : null}

          <div className="relative text-center text-sm">
            <span className="text-muted-foreground">{i18n._(msg`or`)}</span>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={onUsePassword}
            disabled={isLoading}
          >
            {i18n._(msg`Use password instead`)}
          </Button>

          <Button
            variant="ghost"
            className="text-muted-foreground w-full"
            onClick={onUseDifferentEmail}
            disabled={isLoading}
          >
            {i18n._(msg`Use a different email`)}
          </Button>
        </div>
      </div>

      <AuthPageFooter
        prompt={i18n._(msg`Don't have an account?`)}
        to={signupHref}
        label={i18n._(msg`Sign up`)}
      />
    </div>
  );
}
